import { useRef, useState } from "react";
import { ArrowRight, Loader2, Monitor } from "lucide-react";
import { parseDesktopAuthorization, validateDesktopAuthorizationRedirect } from "@/lib/desktop-auth";
import Login from "./login";
import { NewTaskDotBackground } from "./canvas/NewTaskDotBackground";
import "./workspace-auth.css";

interface DesktopAuthorizeProps {
  user: { name: string; email: string } | null;
  onLogin: () => void | Promise<void>;
}

export default function DesktopAuthorize({ user, onLogin }: DesktopAuthorizeProps) {
  const request = parseDesktopAuthorization(window.location.search);
  const [error, setError] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const submitting = useRef(false);

  async function connect() {
    if (!request || !user || submitting.current || cancelled) return;
    submitting.current = true;
    setConnecting(true);
    setError("");
    try {
      const response = await fetch("/api/desktop-auth/authorize", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(request),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(response.status === 401
          ? "Your session expired. Refresh this page to sign in again."
          : body.message || "Couldn't connect Admoji Desktop. Please try again.");
        return;
      }
      const redirect = validateDesktopAuthorizationRedirect(request, body.redirectUrl);
      if (!redirect) throw new Error("Invalid desktop callback");
      window.location.assign(redirect);
    } catch {
      setError("Couldn't connect Admoji Desktop. Reopen sign-in from the desktop app and try again.");
    } finally {
      submitting.current = false;
      setConnecting(false);
    }
  }

  // Keep the validated authorization URL intact through both login methods.
  // Signing in alone never grants the desktop a session.
  if (request && !user && !cancelled) return <Login onLogin={onLogin} variant="desktop" />;

  return (
    <main className="workspace-auth" data-testid="desktop-authorize-page">
      <NewTaskDotBackground />
      <div className="workspace-auth__shell">
        <div className="workspace-auth__logo"><img src="/admoji-logo.png" alt="Admoji" width={128} /></div>
        <section className="workspace-auth__card" aria-labelledby="desktop-authorize-title" data-ripple-block>
          <header className="workspace-auth__heading">
            <p className="workspace-auth__eyebrow">ADMOJI DESKTOP</p>
            <h1 id="desktop-authorize-title">{!request ? "Invalid sign-in link." : cancelled ? "Connection cancelled." : "Connect your workspace."}</h1>
            <p className="workspace-auth__intro">
              {!request
                ? "Open Admoji Desktop and start sign-in again."
                : cancelled
                  ? "You can close this tab. Start sign-in again in Admoji Desktop when you're ready."
                  : "Use your Admoji account in the desktop app, with the same brands, products, projects, creatives, and credits."}
            </p>
          </header>
          {request && user && !cancelled && (
            <div className="workspace-auth__form" aria-busy={connecting}>
              <div className="workspace-auth__desktop-account" data-testid="desktop-authorize-account">
                <Monitor aria-hidden="true" />
                <div><strong>{user.name || "Admoji account"}</strong><span>{user.email}</span></div>
              </div>
              <p className="workspace-auth__intro">Only connect if you started sign-in from Admoji Desktop on this computer.</p>
              {error && <div className="workspace-auth__error" role="alert">{error}</div>}
              <button className="workspace-auth__submit" disabled={connecting} onClick={() => void connect()} data-testid="button-connect-desktop">
                {connecting ? <><Loader2 className="workspace-auth__spinner" aria-hidden="true" />Connecting…</> : <>Connect Admoji Desktop<ArrowRight aria-hidden="true" /></>}
              </button>
              <button className="workspace-auth__secondary" disabled={connecting} onClick={() => setCancelled(true)}>Cancel</button>
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
